import React, { useEffect } from 'react';
import { CheckCircle, XCircle, X, Info, AlertTriangle } from 'lucide-react';

const TOAST_DURATION = 4500;

const types = {
  success: {
    icon: CheckCircle,
    bg: 'bg-surface border border-moss/10 shadow-[0_12px_40px_rgba(96,108,56,0.1)]',
    iconBg: 'bg-moss/15 text-moss',
    titleClass: 'text-pakistan font-bold text-sm',
    messageClass: 'text-pakistan/60 text-xs font-medium',
    accent: 'border-l-[3px] border-l-moss',
  },
  error: {
    icon: XCircle,
    bg: 'bg-surface border border-tiger/20 shadow-[0_12px_40px_rgba(188,108,37,0.12)]',
    iconBg: 'bg-tiger/15 text-tiger',
    titleClass: 'text-pakistan font-bold text-sm',
    messageClass: 'text-pakistan/60 text-xs font-medium',
    accent: 'border-l-[3px] border-l-tiger',
  },
  warning: {
    icon: AlertTriangle,
    bg: 'bg-surface border border-earth/25 shadow-[0_12px_40px_rgba(221,161,94,0.1)]',
    iconBg: 'bg-earth/20 text-tiger',
    titleClass: 'text-pakistan font-bold text-sm',
    messageClass: 'text-pakistan/60 text-xs font-medium',
    accent: 'border-l-[3px] border-l-earth',
  },
  info: {
    icon: Info,
    bg: 'bg-surface border border-moss/15 shadow-[0_12px_40px_rgba(96,108,56,0.1)]',
    iconBg: 'bg-earth/15 text-moss',
    titleClass: 'text-pakistan font-bold text-sm',
    messageClass: 'text-pakistan/60 text-xs font-medium',
    accent: 'border-l-[3px] border-l-earth',
  },
};

const ToastItem = ({ id, type = 'success', title, message, onDismiss }) => {
  const config = types[type] || types.success;
  const Icon = config.icon;

  useEffect(() => {
    const t = setTimeout(() => onDismiss(id), TOAST_DURATION);
    return () => clearTimeout(t);
  }, [id, onDismiss]);

  return (
    <div
      role="alert"
      className={`flex items-start gap-3 p-4 rounded-2xl ${config.bg} ${config.accent} animate-slideInRight pointer-events-auto max-w-sm w-full backdrop-blur-sm`}
    >
      <div className={`flex-shrink-0 w-9 h-9 rounded-xl flex items-center justify-center ${config.iconBg}`}>
        <Icon size={18} />
      </div>
      <div className="flex-1 min-w-0 pt-0.5">
        <p className={config.titleClass}>{title}</p>
        {message && (
          <p className={`mt-0.5 leading-relaxed ${config.messageClass}`}>{message}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onDismiss(id)}
        className="flex-shrink-0 p-1.5 rounded-lg text-pakistan/40 hover:text-pakistan hover:bg-moss/10 transition-colors"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
    </div>
  );
};

const ToastContainer = ({ toasts, onDismiss }) => (
  <div
    className="fixed top-5 right-5 z-[9999] flex flex-col gap-2.5 pointer-events-none w-full max-w-sm"
    aria-live="polite"
  >
    <div className="flex flex-col gap-2.5 pointer-events-auto">
      {toasts.map((t) => (
        <ToastItem
          key={t.id}
          id={t.id}
          type={t.type}
          title={t.title}
          message={t.message}
          onDismiss={onDismiss}
        />
      ))}
    </div>
  </div>
);

export default ToastContainer;
export { ToastItem };
